import { coveredFee } from './coveredFee.js';

export const STARTER_BILLING_PLAN = 'STARTER';
export const CUSTOM_BILLING_PLAN = 'CUSTOM';

// This module holds NO fee rate of its own (PE-2235 B4). Rates come from the
// record's stored feeAmounts or, when the caller passes `resolveFeeRates`, from the
// rate the backend resolves for that record (negotiated echo, the entitlement
// catalog, or stored). The dashboard passes a resolver built on
// @charitystack/entitlements; this package may not import the catalog (donor apps
// bundle it), so a hand-copied rate here could only go stale — the old STARTER
// constants did (0.05 platform after the catalog moved to 0.04).

// Not a catalog rate: the ACH fee cap every backend defaults to when a record has
// no achMaxFee (edit-donor-details-backend and transactions-backend both use 25).
const DEFAULT_ACH_MAX_FEE = 25;

const FEE_AMOUNT_FIELDS = [
  'percentFee',
  'fixedFee',
  'processingPercentFee',
  'processingFixedFee',
  'platformPercentFee',
  'platformFixedFee',
  'achMaxFee',
  'checkoutBumpCSSharePercent',
];

export const parseFeeNumber = (value, fallback = 0) => {
  const parsed = Number.parseFloat(String(value ?? ''));
  return Number.isFinite(parsed) ? parsed : fallback;
};

const hasValue = (value) => value !== undefined && value !== null && value !== '';

const isPlainObject = (value) =>
  value !== null && typeof value === 'object' && !Array.isArray(value);

const cleanMap = (value) => {
  if (!isPlainObject(value)) return {};
  return Object.fromEntries(
    Object.entries(value).filter(([, nested]) => hasValue(nested)),
  );
};

export const normalizeBillingPlan = (plan) => {
  const normalized = String(plan ?? '').trim().toUpperCase().replace(/[- ]/g, '_');
  if (!normalized) return '';
  return normalized === STARTER_BILLING_PLAN ? STARTER_BILLING_PLAN : CUSTOM_BILLING_PLAN;
};

export const getFeeAmounts = (source) => {
  const nestedFeeAmounts = cleanMap(source?.feeAmounts);
  if (Object.keys(nestedFeeAmounts).length > 0) return nestedFeeAmounts;

  return Object.fromEntries(
    FEE_AMOUNT_FIELDS
      .filter((field) => hasValue(source?.[field]))
      .map((field) => [field, source[field]]),
  );
};

const getDefaultBillingPlan = () => {
  return {
    billingPlan: CUSTOM_BILLING_PLAN,
    billingPlanSource: 'DEFAULT_CUSTOM',
  };
};

// PE-1630 §1.6 — dev/telemetry guard. Fires when this helper is handed a STARTER
// record that has no fee data and no resolved rate, so the estimate has no rate to
// use. The dashboard passes `resolveFeeRates`, which always resolves a STARTER
// record from the catalog, so this should never fire; if it does, some caller is
// estimating without the resolver. Dev-only + best-effort: never throws and never
// runs in prod, so it can't affect a donor/merchant estimate.
const warnUncatalogedStarterFallback = (source) => {
  try {
    // Most FE consumers are Vite browser builds where `process` is undefined and
    // `import.meta.env.DEV` is the dev signal; Node/test contexts use NODE_ENV.
    // Check both so the guard actually fires in browser dev (donation-elements,
    // checkout, hosted-page, portal) as well as under vitest/node.
    let isDev = false;
    try {
      if (typeof import.meta !== 'undefined' && import.meta.env) {
        isDev = import.meta.env.DEV === true || import.meta.env.MODE === 'development';
      }
    } catch {
      /* import.meta not available in this module system — fall through to process.env */
    }
    if (
      !isDev &&
      typeof process !== 'undefined' &&
      process?.env?.NODE_ENV &&
      process.env.NODE_ENV !== 'production'
    ) {
      isDev = true;
    }
    if (!isDev) return;
    // eslint-disable-next-line no-console
    console.warn(
      '[subscriptionFeePolicy] STARTER record with no feeAmounts and no resolved rate — ' +
        'the estimate has no rate (§1.6). Pass resolveFeeRates (the dashboard resolves ' +
        'STARTER from the entitlement catalog). billingPlanSource=' +
        String(source?.billingPlanSource ?? 'n/a'),
    );
  } catch {
    /* never let a telemetry guard affect a fee estimate */
  }
};

/**
 * The platform fee the covered-fee rule should see for a record, resolved the way
 * the backends' resolve_billing_fee_policy resolves it, so a preview here matches
 * the fee a portal edit or create-donation charges:
 *   - split fields recorded → those values;
 *   - legacy-only record (percentFee/fixedFee, no split) on a non-STARTER plan →
 *     0, because the backend's legacy branch carries no platform fee;
 *   - otherwise unknown (null), which the shared rule treats as fee-paying (flat).
 *     A legacy-only STARTER record takes the catalog platform rate there, never 0.
 */
const resolvePlatformFeeForCoveredFee = ({
  feeAmounts,
  billingPlan,
  platformPercentFee,
  platformFixedFee,
}) => {
  const hasSplit = [
    'processingPercentFee',
    'processingFixedFee',
    'platformPercentFee',
    'platformFixedFee',
  ].some((field) => hasValue(feeAmounts[field]));
  if (hasSplit) {
    return {
      platformPercentFee: hasValue(feeAmounts.platformPercentFee) ? platformPercentFee : null,
      platformFixedFee: hasValue(feeAmounts.platformFixedFee) ? platformFixedFee : null,
    };
  }
  if (hasValue(feeAmounts.percentFee) && billingPlan !== STARTER_BILLING_PLAN) {
    return { platformPercentFee: 0, platformFixedFee: 0 };
  }
  return { platformPercentFee: null, platformFixedFee: null };
};

const RESOLVED_RATE_FIELDS = [
  'processingPercentFee',
  'platformPercentFee',
  'processingFixedFee',
  'platformFixedFee',
];

const isResolvedRates = (rates) =>
  isPlainObject(rates) &&
  RESOLVED_RATE_FIELDS.every((field) => Number.isFinite(Number(rates[field])));

/**
 * `resolvedRates` — the four rates the backend resolves for this record
 * ({ processingPercentFee, platformPercentFee, processingFixedFee, platformFixedFee },
 * numbers). When given they replace the stored feeAmounts and the accounts
 * dashboard rate for the percent, the fixed fee and the platform fee the
 * covered-fee rule reads.
 */
export const getBillingPolicyDetails = (
  source,
  { preferDashboardFees = false, resolvedRates = null, coveredFeePlan = null } = {},
) => {
  const feeAmounts = getFeeAmounts(source);
  const billingDetails = cleanMap(source?.billingDetails);
  const processingFees = cleanMap(billingDetails.processingFees);
  // Fee-tier resolution order (intentional — keep in sync with
  // packages/dashboard/src/utils/dashboardFeeCalculation.ts::getMerchantBillingPlan):
  // 1. billingDetails.processingFees.tier — the V2 nested canonical fee-tier location.
  // 2. billingDetails.plan — LEGACY read (pre-existing): holds the subscription/
  //    entitlement slug (STARTER/PLUS), not the fee tier; kept only for backward
  //    compatibility with older org shapes. Do not extend its use.
  // 3. top-level billingPlan — the backend-derived V1 flat exposure of the same
  //    fee axis as (1); the backend infers it FROM processingFees.tier, so (1)
  //    and (3) describe one axis and cannot legitimately disagree.
  const explicitPlan = normalizeBillingPlan(
    processingFees.tier || billingDetails.plan || source?.billingPlan,
  );
  const defaultPlan = getDefaultBillingPlan();
  const billingPlan = explicitPlan || defaultPlan.billingPlan;
  const billingPlanSource =
    source?.billingPlanSource || (explicitPlan ? 'EXPLICIT' : defaultPlan.billingPlanSource);

  const processingPercentFee = parseFeeNumber(feeAmounts.processingPercentFee);
  const platformPercentFee = parseFeeNumber(feeAmounts.platformPercentFee);
  const processingFixedFee = parseFeeNumber(feeAmounts.processingFixedFee);
  const platformFixedFee = parseFeeNumber(feeAmounts.platformFixedFee);
  const hasSplitPercentFee =
    hasValue(feeAmounts.processingPercentFee) || hasValue(feeAmounts.platformPercentFee);
  const hasSplitFixedFee =
    hasValue(feeAmounts.processingFixedFee) || hasValue(feeAmounts.platformFixedFee);

  // PE-1630 §1.6 — a STARTER record with no resolved rate, no dashboard fees, no
  // split fields and no percentFee has no rate at all; flag it (dev-only) so the
  // otherwise-silent path is observable.
  const hasResolvedRates = isResolvedRates(resolvedRates);
  const usingStarterFallback =
    !hasResolvedRates &&
    !(preferDashboardFees && hasValue(source?.dashboardPercentFee)) &&
    !hasSplitPercentFee &&
    !hasValue(feeAmounts.percentFee) &&
    billingPlan === STARTER_BILLING_PLAN;
  if (usingStarterFallback) {
    warnUncatalogedStarterFallback(source);
  }

  let percentFee = hasResolvedRates
    ? Number(resolvedRates.processingPercentFee) + Number(resolvedRates.platformPercentFee)
    : preferDashboardFees && hasValue(source?.dashboardPercentFee)
      ? parseFeeNumber(source.dashboardPercentFee)
      : hasSplitPercentFee
        ? processingPercentFee + platformPercentFee
        : hasValue(feeAmounts.percentFee)
          ? parseFeeNumber(feeAmounts.percentFee)
          : 0;

  let fixedFee = hasResolvedRates
    ? Number(resolvedRates.processingFixedFee) + Number(resolvedRates.platformFixedFee)
    : preferDashboardFees && hasValue(source?.dashboardFixedFee)
      ? parseFeeNumber(source.dashboardFixedFee)
      : hasSplitFixedFee
        ? processingFixedFee + platformFixedFee
        : hasValue(feeAmounts.fixedFee)
          ? parseFeeNumber(feeAmounts.fixedFee)
          : 0;

  percentFee = Number(percentFee.toFixed(6));
  fixedFee = Number(fixedFee.toFixed(2));

  return {
    billingPlan,
    billingPlanSource,
    billingDetails: {
      ...billingDetails,
      ...(billingPlan && !billingDetails.plan ? { plan: billingPlan } : {}),
    },
    feeAmounts,
    percentFee,
    fixedFee,
    // The covered-fee rule keys on these (gross-up only with no platform fee).
    ...(hasResolvedRates
      ? {
          platformPercentFee: Number(resolvedRates.platformPercentFee),
          platformFixedFee: Number(resolvedRates.platformFixedFee),
        }
      : resolvePlatformFeeForCoveredFee({
          feeAmounts,
          billingPlan,
          platformPercentFee,
          platformFixedFee,
        })),
    achMaxFee:
      parseFeeNumber(feeAmounts.achMaxFee, DEFAULT_ACH_MAX_FEE) || DEFAULT_ACH_MAX_FEE,
    feeAmountsCapturedAt: source?.feeAmountsCapturedAt,
    hasFeeSnapshot: Object.keys(feeAmounts).length > 0,
    // Rule G (PE-2235): { billingPlan, isCatalogPricedTier } as the backend resolves
    // them, or null when the caller cannot tell (the platform-fee half then decides).
    coveredFeePlan: isPlainObject(coveredFeePlan) ? coveredFeePlan : null,
  };
};

// Asks the caller's resolver for the backend's rule-G plan inputs for one record.
const resolveCoveredFeePlanFor = (resolveCoveredFeePlan, record, context) => {
  if (typeof resolveCoveredFeePlan !== 'function' || !record) return null;
  try {
    const plan = resolveCoveredFeePlan(record, context);
    return isPlainObject(plan) ? plan : null;
  } catch {
    return null;
  }
};

// Asks the caller's resolver for the backend-resolved rates of one record. A
// resolver that throws or returns nothing leaves the stored rates in charge.
const resolveRatesFor = (resolveFeeRates, record, context) => {
  if (typeof resolveFeeRates !== 'function' || !record) return null;
  try {
    const rates = resolveFeeRates(record, context);
    return isResolvedRates(rates) ? rates : null;
  } catch {
    return null;
  }
};

/**
 * `resolveFeeRates(record, { recordSource, forSubscriptionEdit })` — optional.
 * Returns the four rates the backend resolves for `record` (see
 * getBillingPolicyDetails), or null to keep the stored rates. `recordSource` is
 * 'subscription' (the subscription's fee snapshot) or 'organization';
 * `forSubscriptionEdit` says the fee will be saved by a subscription edit
 * (edit-donor-details-backend) rather than charged by transactions-backend.
 */
export const getEffectiveSubscriptionFeePolicy = ({
  subscription,
  merchant,
  resolveFeeRates,
  resolveCoveredFeePlan,
  forSubscriptionEdit = Boolean(subscription),
} = {}) => {
  const subscriptionFeeAmounts = getFeeAmounts(subscription);
  if (Object.keys(subscriptionFeeAmounts).length > 0) {
    const context = { recordSource: 'subscription', forSubscriptionEdit };
    return {
      ...getBillingPolicyDetails(subscription, {
        resolvedRates: resolveRatesFor(resolveFeeRates, subscription, context),
        coveredFeePlan: resolveCoveredFeePlanFor(resolveCoveredFeePlan, subscription, context),
      }),
      source: 'subscription',
    };
  }

  if (merchant) {
    const context = { recordSource: 'organization', forSubscriptionEdit };
    return {
      ...getBillingPolicyDetails(merchant, {
        preferDashboardFees: true,
        resolvedRates: resolveRatesFor(resolveFeeRates, merchant, context),
        coveredFeePlan: resolveCoveredFeePlanFor(resolveCoveredFeePlan, merchant, context),
      }),
      source: 'organization',
    };
  }

  return {
    billingPlan: CUSTOM_BILLING_PLAN,
    billingPlanSource: '',
    billingDetails: {},
    feeAmounts: {},
    percentFee: 0,
    fixedFee: 0,
    achMaxFee: DEFAULT_ACH_MAX_FEE,
    hasFeeSnapshot: false,
    source: 'none',
  };
};

/**
 * The donor-covered fee for a subscription or a dashboard-entered payment.
 *
 * Gross-up vs flat and the rounding come from the shared `coveredFee` rule
 * (@charitystack/entitlements) — the same code the checkout displays with and the
 * backends charge with. Rule G (PE-2235): gross up iff the org pays NO platform fee,
 * or its fee-deal billingPlan is CUSTOM and isCatalogPricedTier is false. Pass the
 * platform fee and plan inputs from the policy (`getBillingPolicyDetails`), never
 * re-derive them.
 */
export const calculateSubscriptionFeeAmount = ({
  amount,
  percentFee = 0,
  fixedFee = 0,
  coverFees = true,
  platformPercentFee = null,
  platformFixedFee = null,
  billingPlan = undefined,
  isCatalogPricedTier = undefined,
}) => {
  const baseAmount = parseFeeNumber(amount);
  if (baseAmount <= 0) return 0;

  try {
    return coveredFee({
      amount: baseAmount,
      percent: parseFeeNumber(percentFee),
      fixed: parseFeeNumber(fixedFee),
      platformPercent: platformPercentFee,
      platformFixed: platformFixedFee,
      coverFees: Boolean(coverFees),
      billingPlan,
      isCatalogPricedTier,
    });
  } catch (error) {
    // percent >= 1 cannot be grossed up; keep the historical $0 estimate rather
    // than throwing inside a render.
    if (error instanceof RangeError) return 0;
    throw error;
  }
};

export const normalizePaymentMethodForFees = (method) => {
  const normalized = String(method ?? '').toUpperCase();
  if (normalized.includes('BANK') || normalized === 'ACH') return 'Bank';
  return 'Card';
};

export const calculateSubscriptionPaymentFees = ({
  amount,
  subscription,
  merchant,
  paymentMethod,
  paymentProcessor,
  coverFees = true,
  isEditOfExistingGift = false,
  resolveFeeRates,
  resolveCoveredFeePlan,
}) => {
  const policy = getEffectiveSubscriptionFeePolicy({
    subscription,
    merchant,
    resolveFeeRates,
    resolveCoveredFeePlan,
  });
  const normalizedPaymentMethod = normalizePaymentMethodForFees(
    paymentMethod || subscription?.paymentMethod,
  );
  const normalizedProcessor = String(
    paymentProcessor ||
      subscription?.paymentProcessor ||
      subscription?.defaultPaymentProcessor ||
      merchant?.defaultPaymentProcessor ||
      '',
  ).toUpperCase();

  // NEVER RAISE ON EDIT (2026-10-13): editing an EXISTING gift — a subscription
  // (edit-donor-details-backend) or a transaction (transactions-backend
  // edit-donation) — grosses up only when the shared rule AND the previous plan
  // rule (fee-deal plan not STARTER) agree, so an edit never raises a fee. Mirror
  // that cap here so an edit preview shows the fee that will be saved. A new
  // dashboard payment is charged with the shared rule alone.
  const editCapsGrossUp =
    (Boolean(subscription) || Boolean(isEditOfExistingGift)) &&
    normalizeBillingPlan(policy.billingPlan) === STARTER_BILLING_PLAN;

  let calculatedFees = calculateSubscriptionFeeAmount({
    amount,
    percentFee: policy.percentFee,
    fixedFee: policy.fixedFee,
    coverFees: editCapsGrossUp ? false : coverFees,
    platformPercentFee: policy.platformPercentFee,
    platformFixedFee: policy.platformFixedFee,
    billingPlan: policy.coveredFeePlan?.billingPlan,
    isCatalogPricedTier: policy.coveredFeePlan?.isCatalogPricedTier,
  });

  if (normalizedPaymentMethod === 'Bank') {
    const minBankFee = normalizedProcessor === 'STRIPE' ? 2 : 1;
    calculatedFees = Math.max(minBankFee, calculatedFees);
    calculatedFees = Math.min(policy.achMaxFee, calculatedFees);
  }

  return Number(calculatedFees.toFixed(2));
};

const comparablePolicy = (policy) => ({
  billingPlan: normalizeBillingPlan(policy.billingPlan),
  percentFee: Number(policy.percentFee || 0).toFixed(6),
  fixedFee: Number(policy.fixedFee || 0).toFixed(2),
  achMaxFee: Number(policy.achMaxFee || DEFAULT_ACH_MAX_FEE).toFixed(2),
});

const hasExplicitOrganizationPolicy = (source) => {
  const feeAmounts = getFeeAmounts(source);
  const billingDetails = cleanMap(source?.billingDetails);
  const processingFees = cleanMap(billingDetails.processingFees);

  return (
    Object.keys(feeAmounts).length > 0 ||
    hasValue(processingFees.tier) ||
    hasValue(billingDetails.plan) ||
    hasValue(source?.billingPlan) ||
    hasValue(source?.dashboardPercentFee) ||
    hasValue(source?.dashboardFixedFee)
  );
};

export const getSubscriptionLegacyFeePolicyDetails = ({
  subscription,
  merchant,
  resolveFeeRates,
} = {}) => {
  const subscriptionPolicy = getEffectiveSubscriptionFeePolicy({
    subscription,
    merchant: null,
    resolveFeeRates,
    forSubscriptionEdit: true,
  });
  const organizationPolicy = merchant
    ? {
        ...getBillingPolicyDetails(merchant, {
          preferDashboardFees: true,
          resolvedRates: resolveRatesFor(resolveFeeRates, merchant, {
            recordSource: 'organization',
            forSubscriptionEdit: true,
          }),
        }),
        source: 'organization',
      }
    : null;

  if (subscriptionPolicy.source !== 'subscription' || !organizationPolicy) {
    return {
      isLegacy: false,
      subscriptionPolicy,
      organizationPolicy,
    };
  }

  if (!hasExplicitOrganizationPolicy(merchant)) {
    return {
      isLegacy: false,
      subscriptionPolicy,
      organizationPolicy,
    };
  }

  const isLegacy =
    JSON.stringify(comparablePolicy(subscriptionPolicy)) !==
    JSON.stringify(comparablePolicy(organizationPolicy));

  return {
    isLegacy,
    subscriptionPolicy,
    organizationPolicy,
  };
};

/**
 * True when a subscription's stored values show its base fee was waived: the donor
 * does not cover and the stored fee is 0. Mirrors edit-donor-details-backend
 * `subscription_base_fee_was_waived`. A waived subscription stays waived on edit
 * while the donor keeps not covering.
 */
export const isSubscriptionBaseFeeWaived = (subscription) => {
  if (!isPlainObject(subscription)) return false;
  if (subscription.coverFees === true) return false;
  if (!hasValue(subscription.fees)) return false;
  return Number(subscription.fees) === 0;
};
